/* @flow */
import invariant from 'invariant';
import PropTypes from 'prop-types';
import * as React from 'react';
import AddedDeviceVerifier from 'app/components/AddedDeviceVerifier.jsx';
import AuthAction from 'auth/AuthActions';
import Box from 'components/Box.jsx';
import BoxBody from 'components/BoxBody.jsx';
import BoxHeader from 'components/BoxHeader.jsx';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator.jsx';
import PendingDeviceResendVerification from 'components/PendingDeviceResendVerification.jsx';
import Row from 'components/Row.jsx';
import Container from 'components/Container.jsx';
import ConfirmPhone from 'auth/components/ConfirmPhone.jsx';
import ErrorReporter from 'app/components/ErrorReporter.jsx';
import Heading from 'components/Heading.jsx';
import View from 'components/View.jsx';

function Loading({ title }: { title: string }) {
  return (
    <div>
      <BoxHeader title={title} />
      <BoxBody>
        <CenteredLoadingIndicator />
      </BoxBody>
    </div>
  );
}

function NoRendererError() {
  return (
    <BoxBody>
      <ErrorReporter error="AddedDeviceConfirmationRoute.no_suitable_renderer">
        <Heading>Oops, an error happened.</Heading>
      </ErrorReporter>
    </BoxBody>
  );
}

function InvalidParameters() {
  return (
    <BoxBody>
      <ErrorReporter error="AddedDeviceConfirmationRoute.invalid_parameters">
        <Heading>Oops, looks like something is wrong with the link.</Heading>
      </ErrorReporter>
    </BoxBody>
  );
}

function TokenAuthenticationFail({ deviceId }: { deviceId: number }) {
  return (
    <BoxBody>
      <ErrorReporter error="AddedDeviceConfirmationRoute.token_authentication_fail">
        <Row>
          <Heading>Oops, the link expired.</Heading>
          <PendingDeviceResendVerification deviceId={deviceId} />
        </Row>
      </ErrorReporter>
    </BoxBody>
  );
}

function SmsAuthenticationFail() {
  return (
    <BoxBody>
      <ErrorReporter error="AddedDeviceConfirmationRoute.sms_authentication_fail">
        <Heading>Oops, an error occured.</Heading>
      </ErrorReporter>
    </BoxBody>
  );
}

function SmsAuthentication({ uid, onAuthenticated }: { uid: string, onAuthenticated: Function }) {
  return (
    <BoxBody>
      <ConfirmPhone uid={uid} title={`We sent a code to ${uid}.`} onSuccess={onAuthenticated} />
    </BoxBody>
  );
}

function DevicesVerification({ deviceId }: { deviceId: number }) {
  return (
    <BoxBody>
      <AddedDeviceVerifier pendingSmsDeviceId={deviceId} />
    </BoxBody>
  );
}

type Status =
  | 'initializing'
  | 'invalid_query_params'
  | 'token_authentication'
  | 'token_authentication_fail'
  | 'sms_authentication'
  | 'sms_authentication_success'
  | 'sms_authentication_fail'
  | 'authenticated';

type State = {
  status: Status,
};

/* This component handle authentification before confirming added devices.
 * The expected url query parameters are:
 *
 * device_id - the added device id to verify
 * one of the following:
 *   auth_token   - the auth token to authenticate the user.
 *   auth_sms_uid - the sms device uid use to auth the user with a 4 digit flow.
 *
 * We use an sms uid because we can't send auth token in sms. We need to authenticate
 * the user before verifyng the added device because otherwise anybody could
 * add his own device to another account and verify it (giving him full account acccess).
 * In fact, API will throw 403 error when trying to verify an added device while not authenticated
 * as the user the device was added to.
 *
 * TODO (maybe): AddedDeviceVerifier component does not support verifying added email devices yet.
 * This is safe because r101-api does not allow adding emails devices to other users.
 * AddedDeviceVerifier tracks an unupported_device_type if this ever happens.
 */
class AddedDeviceConfirmationRoute extends React.PureComponent<{}, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  state: State = {
    status: 'initializing',
  };

  componentWillMount() {
    const { authToken, authSmsUid, deviceId } = this.locationParameters();

    if (!deviceId || !(authToken || authSmsUid)) {
      this.setState({ status: 'invalid_query_params' });
      console.error('AddedDeviceConfirmationRoute - invalid_query_params', {
        device_id: !!deviceId,
        auth_token: !!authToken,
        auth_sms_uid: !!authSmsUid,
      });
    } else if (authToken) {
      this.setState({ status: 'token_authentication' });

      Promise.resolve()
        .then(() => AuthAction.signoutWithoutRedirect())
        .then(() => AuthAction.accessTokenSignIn(authToken))
        .then(() => this.setState({ status: 'authenticated' }))
        .catch(() => this.setState({ status: 'token_authentication_fail' }));
    } else if (authSmsUid) {
      this.setState({ status: 'sms_authentication' });

      Promise.resolve()
        .then(() => AuthAction.signoutWithoutRedirect())
        .then(() => this.setState({ status: 'sms_authentication_success' }))
        .catch(() => this.setState({ status: 'sms_authentication_fail' }));
    }
  }

  handleAuthentication = () => {
    this.setState({ status: 'authenticated' });
  };

  locationParameters(): { authToken: ?string, authSmsUid: ?string, deviceId: ?number } {
    const { query } = this.context.router.location;

    return {
      authToken: query.auth_token,
      authSmsUid: query.auth_sms_uid,
      deviceId: query.device_id ? Number(query.device_id) : undefined,
    };
  }

  render() {
    const { authSmsUid, deviceId } = this.locationParameters();

    const template = body => (
      <View center>
        <Container size="medium">
          <Box>{body}</Box>
        </Container>
      </View>
    );

    if (this.state.status === 'invalid_query_params') {
      return template(<InvalidParameters />);
    }

    invariant(deviceId, 'device id required');

    const STATUS_TO_VIEWS = {
      initializing: () => <Loading title="initializing" />,
      // auth_token authentication, exchange the auth token for a session
      token_authentication: () => <Loading title="Connecting to your account" />,
      token_authentication_fail: () => <TokenAuthenticationFail deviceId={deviceId} />,
      // sms_uid authentication, perform 4 digit authentification:
      sms_authentication: () => <Loading title="Loading" />,
      sms_authentication_fail: () => <SmsAuthenticationFail />,
      sms_authentication_success: () => (
        <SmsAuthentication uid={authSmsUid || ''} onAuthenticated={this.handleAuthentication} />
      ),
      // authenticated, show the actual added device verification.
      authenticated: () => <DevicesVerification deviceId={deviceId} />,
    };

    const content = (STATUS_TO_VIEWS[this.state.status] || NoRendererError)();
    return template(content);
  }
}

export default AddedDeviceConfirmationRoute;
